import type { AxiosResponse } from 'axios';

import api from 'api/api.config';

type ApiPromise<T = unknown> = Promise<AxiosResponse<T>>;

export type ProductType =
  | 'ADMISSIONS_VIEW'
  | 'FINANCIAL_AID_VIEW'
  | 'PROSPECT_VIEW'
  | 'RETENTION_VIEW'
  | 'TWO_OCEAN'
  | string;

export interface ProductDto {
  id: string | number;
  name: string;
  description?: string;
  type?: ProductType;
  [key: string]: unknown;
}

export interface ProductRequest {
  name: string;
  description?: string;
  type?: ProductType;
}

export interface ClientProductDto extends ProductDto {
  assignedDate?: string;
}

export const getProducts = ({ userId }: { userId: string }): ApiPromise<ProductDto[]> =>
  api.get(`/${userId}/products?sort=name,asc`);

export interface ProductIdArgs {
  userId: string;
  productId: string | number;
}

export const getProduct = ({ userId, productId }: ProductIdArgs): ApiPromise<ProductDto> =>
  api.get(`/${userId}/products/${productId}`);

export interface CreateProductArgs {
  userId: string;
  productData: ProductRequest;
}

export const createProduct = ({
  userId,
  productData,
}: CreateProductArgs): ApiPromise<ProductDto> =>
  api.post(`/${userId}/products`, JSON.stringify(productData));

export interface ModifyProductArgs {
  userId: string;
  productId: string | number;
  productData: ProductRequest;
}

export const modifyProduct = ({
  userId,
  productId,
  productData,
}: ModifyProductArgs): ApiPromise<ProductDto> =>
  api.put(`/${userId}/products/${productId}`, JSON.stringify(productData));

export interface ClientProductsArgs {
  userId: string;
  clientId: string | number;
  sort?: string;
}

export const getClientProducts = ({
  userId,
  clientId,
  sort,
}: ClientProductsArgs): ApiPromise<ClientProductDto[]> => {
  const queryString = sort ? `?size=1000&sort=${sort}` : '?size=1000';

  return api.get(`/${userId}/clients/${clientId}/products${queryString}`);
};

export interface ClientProductIdArgs {
  userId: string;
  clientId: string | number;
  productId: string | number;
}

export const getClientProduct = ({
  userId,
  clientId,
  productId,
}: ClientProductIdArgs): ApiPromise<ClientProductDto> =>
  api.get(`/${userId}/clients/${clientId}/products/${productId}`);

export const assignProduct = ({
  userId,
  clientId,
  productId,
}: ClientProductIdArgs): ApiPromise<ClientProductDto> =>
  api.put(`/${userId}/clients/${clientId}/products/${productId}`);

export const unassignProduct = ({
  userId,
  clientId,
  productId,
}: ClientProductIdArgs): ApiPromise<void> =>
  api.delete(`/${userId}/clients/${clientId}/products/${productId}`);
